import { Outlet, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { contactEmail, missingContact } from "@/lib/contact";
import {
  LayoutDashboard, User2, LogOut, ShoppingCart,
  PanelLeftClose, PanelLeft, Menu, X, ChevronLeft,
} from "lucide-react";
import { motion } from "framer-motion";
import SiteFooter from "@/components/SiteFooter";

const COLLAPSE_KEY = "dharmatn.customerNavCollapsed";

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [cartCount, setCartCount] = useState(0);
  // Persisted so the choice survives navigation and reloads — a sidebar that silently re-expands
  // on every page change is worse than one that doesn't collapse at all.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const toggleCollapsed = () => setCollapsed(c => {
    localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
    return !c;
  });
  // The sidebar is `hidden md:flex`, so on a phone held upright there was no navigation on screen
  // at all — no dashboard, no profile, no sign out. Turning the phone sideways crossed the 768px
  // breakpoint and the sidebar appeared, which is what made it look like a rendering bug rather
  // than a missing control. This drawer is that navigation, at every width below `md`.
  const [menuOpen, setMenuOpen] = useState(false);

  // The wizard's two-pane form/preview split needs the full width, so it opts out of the centred
  // reading column the other pages use. It keeps the sidebar either way.
  const fullBleed = loc.pathname.startsWith("/customer/wizard");
  // The dashboard is the root of this section — there is nothing above it to go back to. The
  // wizard is excluded because it carries its own "back to dashboard" link in its own top bar.
  const showBack = loc.pathname !== "/customer" && !fullBleed;

  /**
   * Go back the way the customer came, without ever leaving the app.
   *
   * `nav(-1)` alone would walk out of the site entirely when this page was opened directly — from
   * a link, a bookmark, or a refresh — which is worse than no button. React Router tracks its own
   * position in `history.state.idx`, so a zero there means "nothing of ours behind this" and the
   * dashboard is the honest destination.
   */
  const goBack = () => {
    if (window.history.state?.idx > 0) nav(-1);
    else nav("/customer");
  };

  useEffect(() => {
    const load = () => api.get("/cart").then(r => setCartCount(r.data?.items?.length || 0)).catch(() => {});
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [loc.pathname]);

  // Tapping a destination should navigate *and* get the drawer out of the way.
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  // Escape closes it, same as any other modal surface.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const NavItem = ({ to, icon: I, children, testid, end = false, label, badge = 0, compact = false }) => {
    const active = end ? loc.pathname === to : loc.pathname.startsWith(to);
    return (
      <Link
        to={to} data-testid={testid}
        title={compact ? label : undefined}
        className={`relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          compact ? "justify-center" : ""
        } ${
          active
            ? "bg-white/10 text-white"
            : "text-[#A0AABF] hover:text-white hover:bg-white/5"
        }`}
      >
        <I className="w-4 h-4 shrink-0" strokeWidth={1.5} />
        {!compact && (
          <span className="flex items-center gap-2">
            {children}
            {badge > 0 && (
              <span data-testid="cart-badge" className="bg-[#CBA96C] text-[#080C16] text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge}</span>
            )}
          </span>
        )}
        {/* Collapsed, the label that used to carry the count isn't rendered — so the count moves
            onto the icon. Without this the sidebar simply stopped reporting the cart the moment
            it was collapsed, which is why the desktop showed no total while the phone did. */}
        {compact && badge > 0 && (
          <span data-testid="cart-badge-collapsed"
            className="absolute top-0.5 right-0.5 bg-[#CBA96C] text-[#080C16] text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {badge}
          </span>
        )}
      </Link>
    );
  };

  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  // One definition, rendered in both the sidebar and the drawer, so the two can never offer
  // different destinations.
  const navLinks = (compact) => (
    <>
      <NavItem to="/customer" icon={LayoutDashboard} testid={compact ? "nav-customer-dashboard" : "nav-customer-dashboard"} label="Dashboard" compact={compact} end>Dashboard</NavItem>
      {/* Neither Affidavits nor New Document is a menu entry. Both catalogues are reached from the
          dashboard's "Start a new document" section, which is where someone who wants a document is
          already looking — a nav item duplicating it made the menu a second, competing route to the
          same place. The /customer/new route still works and is still linked from the dashboard and
          the wizard; it just isn't a top-level destination. */}
      <NavItem to="/customer/cart" icon={ShoppingCart} testid="nav-customer-cart" label="Cart" badge={cartCount} compact={compact}>Cart</NavItem>
      <NavItem to="/customer/profile" icon={User2} testid="nav-customer-profile" label="Profile" compact={compact}>Profile</NavItem>
    </>
  );

  // Leave the protected route *before* clearing the session. `Protected` in App.js sends a null
  // user to /login, so clearing first re-renders this still-mounted /customer route and that
  // redirect wins the race — which is why signing out used to land on the login page, not home.
  // `replace` also keeps the signed-out page out of history, so Back doesn't bounce through it.
  const signOut = async () => { nav("/", { replace: true }); await logout(); };

  // **A customer missing their name, email or mobile is sent to their profile and kept there.**
  // Google and Facebook authenticate; they do not always supply these (a Facebook account made with
  // a phone number has no email), and all three are needed for billing and every message we send —
  // see `@/lib/contact`. The mobile in particular is
  // how they are reached about their own case, it is what unlocks the documents they download
  // (`customer_pdf_passwords`), and it is half of what fulfillment waits on
  // (`core.lifecycle.missing_customer_info`) — so it is asked for on arrival rather than
  // discovered at checkout, after the drafting work, which is where the demand used to land.
  //
  // One check covers every customer route because this layout owns them all, the wizard included.
  // The profile page is the single exception, or there would be nowhere to go and fix it. Placed
  // below every hook and expressed as a redirect rather than an effect, so no protected page gets
  // a render in before it. The server enforces the same rule on `PUT /auth/profile`; this is the
  // part that makes it answerable instead of a dead end.
  const needsDetails = !!user && missingContact(user).length > 0;
  if (needsDetails && loc.pathname !== "/customer/profile") {
    return <Navigate to="/customer/profile" replace />;
  }

  return (
    <div className="min-h-screen bg-[#080C16] text-[#F9F8F6] flex">
      {/* Sidebar (md and up) */}
      <aside className={`hidden md:flex flex-col border-r border-white/5 bg-[#0B111C] p-5 sticky top-0 h-screen transition-[width] duration-200 ${collapsed ? "w-20" : "w-64"}`}>
        <div className={`flex items-center mb-10 ${collapsed ? "justify-center" : "justify-between gap-2"}`}>
          {!collapsed && (
            <Link to="/customer" className="flex items-center gap-2 font-serif-display text-lg tracking-tight">
              <BrandMark /> DharmaTN
            </Link>
          )}
          <button
            data-testid="nav-collapse-toggle"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            aria-expanded={!collapsed}
            className="p-1.5 rounded-lg text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors"
          >
            {collapsed ? <PanelLeft className="w-4 h-4" strokeWidth={1.5} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
        <nav className="space-y-1 flex-1">{navLinks(collapsed)}</nav>
        <div className="border-t border-white/5 pt-4">
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div title={collapsed ? `${user?.name} · ${contactEmail(user)}` : undefined} className="w-8 h-8 rounded-full bg-[#CBA96C] text-[#080C16] flex items-center justify-center font-medium text-xs shrink-0">{initials}</div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{user?.name}</div>
                <div className="text-[11px] text-[#6B7280] truncate">{contactEmail(user)}</div>
              </div>
            )}
          </div>
          <button
            data-testid="nav-logout-icon"
            onClick={signOut}
            title={collapsed ? "Sign out" : undefined}
            className={`mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            <LogOut className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />{!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="h-14 flex items-center justify-between px-3">
          <div className="flex items-center gap-1">
            <button
              data-testid="nav-mobile-menu" onClick={() => setMenuOpen(true)}
              aria-label="Open menu" aria-expanded={menuOpen}
              className="p-2 rounded-lg text-white hover:bg-white/5 transition-colors"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <Link to="/customer" className="font-serif-display text-base px-1">DharmaTN</Link>
          </div>
          <Link to="/customer/cart" data-testid="nav-cart-icon-mobile" className="relative p-2">
            <ShoppingCart className="w-5 h-5 text-white" />
            {cartCount > 0 && <span className="absolute top-0 right-0 bg-[#CBA96C] text-[#080C16] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>}
          </Link>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <div
            data-testid="nav-drawer-scrim" onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: -280 }} animate={{ x: 0 }} transition={{ duration: 0.2, ease: "easeOut" }}
            data-testid="nav-drawer"
            className="absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] bg-[#0B111C] border-r border-white/10 p-5 flex flex-col"
          >
            <div className="flex items-center justify-between mb-8">
              <Link to="/customer" className="flex items-center gap-2 font-serif-display text-lg tracking-tight">
                <BrandMark /> DharmaTN
              </Link>
              <button
                data-testid="nav-drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu"
                className="p-1.5 rounded-lg text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="space-y-1 flex-1">{navLinks(false)}</nav>
            <div className="border-t border-white/5 pt-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#CBA96C] text-[#080C16] flex items-center justify-center font-medium text-xs shrink-0">{initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{user?.name}</div>
                  <div className="text-[11px] text-[#6B7280] truncate">{contactEmail(user)}</div>
                </div>
              </div>
              <button
                data-testid="nav-drawer-logout" onClick={signOut}
                className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} /> Sign out
              </button>
            </div>
          </motion.aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className={fullBleed ? "" : "max-w-6xl mx-auto px-6 md:px-10 py-6 md:py-14"}>
          {/* Every page below the dashboard gets a way back that doesn't depend on the phone's
              own back gesture — which is easy to miss, and on iOS is a swipe rather than a
              button. Rendered here so no page can forget it. */}
          {showBack && (
            <button
              data-testid="page-back" onClick={goBack}
              className="inline-flex items-center gap-1 mb-5 text-sm text-[#A0AABF] hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Back
            </button>
          )}
          <motion.div
            key={loc.pathname}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Outlet />
          </motion.div>
        </div>
        {/* Outside the centred reading column so it spans the full width even on the wizard,
            which opts out of that column. */}
        <SiteFooter className={fullBleed ? "" : "px-6 md:px-10"} />
      </main>
    </div>
  );
}
